import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError, downloadProductionReport, downloadProductionHistory } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmProvider";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Fire, Plus, Trash, CheckCircle, Clock, XCircle, DownloadSimple, ArrowSquareOut, Copy, PencilSimple, Warning } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const MODEL_COLORS = { "12L": "#EA580C", "18L": "#0891B2", "25L": "#16A34A", "Electric Kettle": "#CA8A04" };
const modelColor = (m) => MODEL_COLORS[m] || "#71717A";
const monthLabel = (m) => {
  if (!m) return m;
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleString([], { month: "short", year: "numeric" });
};

const fmt = (iso) => new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });

// Oven models are size codes (e.g. "12L") shown as "12L Oven"; other products (e.g. "Electric Kettle") use their name as-is.
const productLabel = (m) => (m && /^\d+(\.\d+)?\s*L$/i.test(m.trim()) ? `${m} Oven` : m);

export default function Production() {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm("production", "edit");
  const confirm = useConfirm();
  const [ovens, setOvens] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [runs, setRuns] = useState([]);
  const [boms, setBoms] = useState([]);
  const [bomOpen, setBomOpen] = useState(false);
  const [activeModel, setActiveModel] = useState(null);
  const [bomName, setBomName] = useState("");
  const [lines, setLines] = useState([]);
  const [savingBom, setSavingBom] = useState(false);
  const [editBomId, setEditBomId] = useState(null);
  const [prodModel, setProdModel] = useState("12L");
  const [prodBomId, setProdBomId] = useState("");
  const [prodQty, setProdQty] = useState(1);
  const [producing, setProducing] = useState(false);
  const [report, setReport] = useState([]);
  const [reportMonth, setReportMonth] = useState("all");
  const [historyFilter, setHistoryFilter] = useState("all");
  const [orderQuery, setOrderQuery] = useState("");
  const [recordRun, setRecordRun] = useState(null);
  const [recordQty, setRecordQty] = useState(1);
  const [recording, setRecording] = useState(false);
  const [adjustRun, setAdjustRun] = useState(null);
  const [adjustDelta, setAdjustDelta] = useState("");
  const [rejectRun, setRejectRun] = useState(null);
  const [rejectMaterialId, setRejectMaterialId] = useState("");
  const [rejectQty, setRejectQty] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const load = () => {
    api.get("/ovens").then((r) => setOvens(r.data)).catch(() => {});
    api.get("/materials").then((r) => setMaterials(r.data)).catch(() => {});
    api.get("/production").then((r) => setRuns(r.data)).catch(() => {});
    api.get("/production/report").then((r) => setReport(r.data)).catch(() => {});
    api.get("/boms").then((r) => setBoms(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const bomsFor = (model) => boms.filter((b) => b.model === model);

  const openNewBom = (model) => {
    setEditBomId(null);
    setActiveModel(model);
    setBomName("");
    setLines([{ material_id: "", qty_per_unit: "" }]);
    setBomOpen(true);
  };

  const openEditBom = (bom) => {
    setEditBomId(bom.id);
    setActiveModel(bom.model);
    setBomName(bom.name);
    setLines(bom.lines.map((l) => ({ material_id: l.material_id, qty_per_unit: l.qty_per_unit })));
    setBomOpen(true);
  };

  const saveBom = async () => {
    if (!bomName.trim()) return toast.error("Give the BOM a name");
    const clean = lines.filter((l) => l.material_id && Number(l.qty_per_unit) > 0)
      .map((l) => ({ material_id: l.material_id, qty_per_unit: Number(l.qty_per_unit) }));
    if (clean.length === 0) return toast.error("Add at least one material with a quantity");
    setSavingBom(true);
    try {
      const payload = { model: activeModel, name: bomName.trim(), lines: clean };
      if (editBomId) {
        await api.put(`/boms/${editBomId}`, payload);
        toast.success(`BOM "${bomName.trim()}" updated`);
      } else {
        await api.post("/boms", payload);
        toast.success(`BOM "${bomName.trim()}" added for ${activeModel} oven`);
      }
      setBomOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSavingBom(false);
    }
  };

  const removeBom = async (bom) => {
    if (!(await confirm({ title: "Delete BOM", description: `Delete BOM "${bom.name}" (${bom.model})?`, confirmText: "Delete" }))) return;
    try { await api.delete(`/boms/${bom.id}`); toast.success("BOM deleted"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const duplicateBom = (bom) => {
    setEditBomId(null);
    setActiveModel(bom.model);
    setBomName(`${bom.name} (copy)`);
    setLines(bom.lines.map((l) => ({ material_id: l.material_id, qty_per_unit: l.qty_per_unit })));
    setBomOpen(true);
  };

  const produce = async () => {
    if (!prodBomId) return toast.error("Select a BOM to build from");
    if (Number(prodQty) <= 0) return toast.error("Enter a valid quantity");
    setProducing(true);
    try {
      const res = await api.post("/production", { model: prodModel, quantity: Number(prodQty), bom_id: prodBomId });
      toast.success(`Started build of ${res.data.quantity} × ${res.data.model} (BOM: ${res.data.bom_name}) — materials blocked. Record ovens as they're made.`);
      setProdQty(1);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setProducing(false);
    }
  };

  const openRecord = (run) => { setRecordRun(run); setRecordQty(1); };

  const recordMade = async () => {
    if (!recordRun) return;
    const remaining = recordRun.quantity - (recordRun.made || 0);
    if (Number(recordQty) <= 0 || Number(recordQty) > remaining) return toast.error(`Enter 1–${remaining} ovens`);
    setRecording(true);
    try {
      await api.post(`/production/${recordRun.id}/complete`, { quantity: Number(recordQty) });
      toast.success(`Recorded ${recordQty} × ${recordRun.model} made — stock deducted, block reduced`);
      setRecordRun(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setRecording(false);
    }
  };

  const adjustMade = async () => {
    if (!adjustRun) return;
    const d = parseInt(adjustDelta, 10);
    if (!d) return toast.error("Enter a non-zero change (e.g. 2 or -1)");
    try {
      await api.post(`/production/${adjustRun.id}/adjust`, { delta: d });
      toast.success(`Planned quantity adjusted by ${d > 0 ? "+" : ""}${d}`);
      setAdjustRun(null); setAdjustDelta("");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const cancelRun = async (run) => {
    if (!(await confirm({ title: "Cancel Build", description: `Cancel this ${run.model} build? Ovens already made stay counted; the remaining blocked materials are released back to available stock.`, confirmText: "Cancel Build" }))) return;
    try {
      await api.post(`/production/${run.id}/cancel`);
      toast.success(`Build cancelled — remaining block released`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const matUnit = (mid) => materials.find((m) => m.id === mid)?.unit || "";
  const matAvailable = (mid) => {
    const m = materials.find((x) => x.id === mid);
    return m ? (m.available ?? (m.current_stock - (m.reserved || 0))) : null;
  };

  const openReject = (run) => {
    setRejectRun(run);
    setRejectMaterialId(run.lines?.[0]?.material_id || "");
    setRejectQty("");
    setRejectReason("");
    setRejecting(false);
  };

  const submitReject = async () => {
    if (!rejectRun) return;
    if (!rejectMaterialId) return toast.error("Select the rejected material");
    const q = Number(rejectQty);
    if (!q || q <= 0) return toast.error("Enter the rejected quantity");
    setRejecting(true);
    try {
      const res = await api.post(`/production/${rejectRun.id}/reject`, {
        material_id: rejectMaterialId,
        quantity: q,
        reason: rejectReason.trim() || null,
      });
      toast.success(`${q} ${matUnit(rejectMaterialId)} scrapped — removed from stock, order compensated`);
      const d = res.data || {};
      if (d.low_stock) {
        toast.warning(`Low stock: ${d.material_name} is now ${d.available} ${d.unit} (min ${d.min_stock}). Consider reordering.`, { duration: 8000 });
      }
      setRejectRun(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setRejecting(false);
    }
  };

  const prodBoms = bomsFor(prodModel);

  return (
    <>
      <PageHeader title="Production" subtitle="Oven Assembly & Consumption" />
      <div className="p-4 sm:p-8 space-y-6 sm:space-y-8">
        {/* Bill of materials per model */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {ovens.map((o) => {
            const list = bomsFor(o.model);
            return (
            <div key={o.model} className="bg-white border border-zinc-300" data-testid={`oven-card-${o.model}`}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-300 bg-zinc-50">
                <div className="flex items-center gap-2">
                  <Fire size={20} weight="fill" className="text-primary" />
                  <h3 className="font-heading font-black text-xl tracking-tighter">{productLabel(o.model).toUpperCase()}</h3>
                </div>
                {canEdit && (<Button size="sm" variant="outline" onClick={() => openNewBom(o.model)} data-testid={`add-bom-${o.model}`} className="rounded-none border-zinc-300 text-xs uppercase font-bold">
                  <Plus size={14} weight="bold" className="mr-1" /> New BOM
                </Button>)}
              </div>
              <div className="p-6">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">Bill of Materials ({list.length})</p>
                {list.length === 0 ? (
                  <p className="text-sm text-zinc-400">No BOMs yet. Add one to build this model.</p>
                ) : (
                  <ul className="space-y-2" data-testid={`bom-list-${o.model}`}>
                    {list.map((b) => (
                      <li key={b.id} className="border border-zinc-200" data-testid={`bom-item-${b.id}`}>
                        <div className="flex items-center justify-between px-3 py-2 bg-zinc-50">
                          <button onClick={() => window.open(`/boms/${b.id}`, "_blank", "noopener")} data-testid={`bom-open-${b.id}`} title="Open full BOM in a new window" className="flex items-center gap-2 text-sm font-medium text-zinc-800 flex-1 text-left hover:text-primary transition-colors duration-100">
                            {b.name}
                            <span className="text-[11px] text-zinc-400 font-normal">· {b.lines.length} items</span>
                            <ArrowSquareOut size={13} weight="bold" className="text-zinc-400" />
                          </button>
                          <div className="flex items-center gap-1">
                            <button onClick={() => openEditBom(b)} data-testid={`edit-bom-${b.id}`} title="Edit BOM" className="text-zinc-500 hover:text-primary p-1"><PencilSimple size={14} weight="bold" /></button>
                            <button onClick={() => duplicateBom(b)} data-testid={`duplicate-bom-${b.id}`} title="Duplicate as new variant" className="text-zinc-500 hover:text-primary p-1"><Copy size={14} weight="bold" /></button>
                            <button onClick={() => removeBom(b)} data-testid={`delete-bom-${b.id}`} title="Delete BOM" className="text-[#DC2626] p-1"><Trash size={14} weight="bold" /></button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          );})}
        </div>

        {/* Produce */}
        <div className="bg-white border border-zinc-300 p-6 hard-shadow-sm">
          <h3 className="font-heading font-bold uppercase tracking-tight text-sm mb-4">Record Production Run</h3>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Product / Model</Label>
              <Select value={prodModel} onValueChange={(v) => { setProdModel(v); setProdBomId(""); }}>
                <SelectTrigger data-testid="production-model-select" className="rounded-none border-zinc-300 w-40 focus:ring-2 focus:ring-primary"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {ovens.map((o) => <SelectItem key={o.model} value={o.model}>{productLabel(o.model)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">BOM</Label>
              <Select value={prodBomId} onValueChange={setProdBomId}>
                <SelectTrigger data-testid="production-bom-select" className="rounded-none border-zinc-300 w-56 focus:ring-2 focus:ring-primary">
                  <SelectValue placeholder={prodBoms.length ? "Select BOM" : "No BOM for this model"} />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {prodBoms.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Quantity</Label>
              <Input type="number" min={1} data-testid="production-qty-input" value={prodQty} onChange={(e) => setProdQty(e.target.value)} className="rounded-none border-zinc-300 font-mono w-32 focus-visible:ring-2 focus-visible:ring-primary" />
            </div>
            {canEdit && (<Button onClick={produce} disabled={producing} data-testid="produce-button" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider transition-colors duration-100">
              <Fire size={16} weight="fill" className="mr-1" /> {producing ? "Starting…" : "Start Build"}
            </Button>)}
          </div>
        </div>

        {/* Production report chart */}
        <div className="bg-white border border-zinc-300" data-testid="production-report">
          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-zinc-300 bg-zinc-50">
            <div>
              <h3 className="font-heading font-bold uppercase tracking-tight text-sm">Finished Ovens by Month</h3>
              <p className="text-[11px] text-zinc-400">Confirmed production per model</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={reportMonth} onValueChange={setReportMonth}>
                <SelectTrigger data-testid="report-month-select" className="rounded-none border-zinc-300 w-44 h-9 text-xs focus:ring-2 focus:ring-primary"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">All months</SelectItem>
                  {[...report].reverse().map((r) => <SelectItem key={r.month} value={r.month}>{monthLabel(r.month)}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => downloadProductionReport("xlsx", reportMonth === "all" ? "" : reportMonth).catch(() => toast.error("Export failed"))} data-testid="report-export-excel" className="rounded-none border-zinc-300 font-bold uppercase tracking-wider text-xs">
                <DownloadSimple size={14} weight="bold" className="mr-1" /> Excel
              </Button>
              <Button variant="outline" size="sm" onClick={() => downloadProductionReport("pdf", reportMonth === "all" ? "" : reportMonth).catch(() => toast.error("Export failed"))} data-testid="report-export-pdf" className="rounded-none border-zinc-300 font-bold uppercase tracking-wider text-xs">
                <DownloadSimple size={14} weight="bold" className="mr-1" /> PDF
              </Button>
            </div>
          </div>
          <div className="p-6">
            {(() => {
              const shown = reportMonth === "all" ? report : report.filter((r) => r.month === reportMonth);
              if (shown.length === 0) {
                return <p className="text-sm text-zinc-400" data-testid="report-empty">No confirmed production {reportMonth === "all" ? "yet. Confirm a build to see monthly trends." : "for this month."}</p>;
              }
              return (
                <>
                  {reportMonth !== "all" && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6" data-testid="report-month-summary">
                      {ovens.map((o) => o.model).map((m) => (
                        <div key={m} className="border border-zinc-200 p-4" data-testid={`report-summary-${m}`}>
                          <div className="flex items-center justify-between gap-1">
                            <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 truncate">{m}</span>
                            <span className="w-3 h-3 shrink-0" style={{ backgroundColor: modelColor(m) }} />
                          </div>
                          <p className="font-mono text-3xl font-bold text-zinc-900 mt-2 tabular">{shown[0][m] ?? 0}</p>
                          <p className="text-[11px] text-zinc-400">units</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ width: "100%", minWidth: 0, height: 320 }} data-testid="production-report-chart">
                    <ResponsiveContainer>
                      <BarChart data={shown.map((r) => ({ ...r, label: monthLabel(r.month) }))} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#71717A" }} axisLine={{ stroke: "#D4D4D8" }} tickLine={false} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#71717A" }} axisLine={{ stroke: "#D4D4D8" }} tickLine={false} />
                        <Tooltip cursor={{ fill: "rgba(9,9,11,0.04)" }} contentStyle={{ borderRadius: 0, border: "1px solid #D4D4D8", fontSize: 12 }} />
                        <Legend wrapperStyle={{ fontSize: 12, textTransform: "uppercase", fontWeight: 700 }} />
                        {ovens.map((o) => <Bar key={o.model} dataKey={o.model} name={o.model} fill={modelColor(o.model)} />)}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        {/* History */}
        <div className="bg-white border border-zinc-300">
          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-zinc-300 bg-zinc-50">
            <h3 className="font-heading font-bold uppercase tracking-tight text-sm">Production History</h3>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                data-testid="history-order-search"
                value={orderQuery}
                onChange={(e) => setOrderQuery(e.target.value)}
                placeholder="Search order no…"
                className="rounded-none border-zinc-300 w-44 h-9 text-xs focus-visible:ring-2 focus-visible:ring-primary"
              />
              <Select value={historyFilter} onValueChange={setHistoryFilter}>
                <SelectTrigger data-testid="history-filter-select" className="rounded-none border-zinc-300 w-40 h-9 text-xs focus:ring-2 focus:ring-primary"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">All entries</SelectItem>
                  <SelectItem value="building">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => downloadProductionHistory("xlsx", historyFilter).catch(() => toast.error("Export failed"))} data-testid="history-export-excel" className="rounded-none border-zinc-300 font-bold uppercase tracking-wider text-xs">
                <DownloadSimple size={14} weight="bold" className="mr-1" /> Excel
              </Button>
              <Button variant="outline" size="sm" onClick={() => downloadProductionHistory("pdf", historyFilter).catch(() => toast.error("Export failed"))} data-testid="history-export-pdf" className="rounded-none border-zinc-300 font-bold uppercase tracking-wider text-xs">
                <DownloadSimple size={14} weight="bold" className="mr-1" /> PDF
              </Button>
            </div>
          </div>
          <div className="divide-y divide-zinc-200" data-testid="production-history">
            {(() => {
              const oq = orderQuery.trim().toLowerCase();
              let visibleRuns = historyFilter === "all" ? runs : runs.filter((r) => (r.status || "building") === historyFilter);
              if (oq) visibleRuns = visibleRuns.filter((r) => (r.order_no || "").toLowerCase().includes(oq));
              if (runs.length === 0) return <p className="p-6 text-sm text-zinc-400">No production runs yet.</p>;
              if (visibleRuns.length === 0) return <p className="p-6 text-sm text-zinc-400" data-testid="history-empty">No matching production runs.</p>;
              return visibleRuns.map((r) => {
              const status = r.status || "building";
              const completed = status === "completed";
              const cancelled = status === "cancelled";
              const made = r.made || 0;
              const remaining = r.quantity - made;
              const pct = r.quantity ? Math.round((made / r.quantity) * 100) : 0;
              const badge = completed
                ? { cls: "bg-[#16A34A]", icon: <CheckCircle size={11} weight="fill" />, text: "Completed" }
                : cancelled
                ? { cls: "bg-zinc-500", icon: <XCircle size={11} weight="fill" />, text: "Cancelled" }
                : { cls: "bg-[#EAB308]", icon: <Clock size={11} weight="fill" />, text: "In Progress" };
              return (
              <div key={r.id} className="px-6 py-4" data-testid={`production-run-${r.id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 flex-wrap">
                    {r.order_no && <span className="font-mono text-xs font-bold text-primary" data-testid={`run-order-${r.id}`}>{r.order_no}</span>}
                    <span className="font-mono text-sm font-bold bg-zinc-900 text-white px-2 py-1">{r.model}</span>
                    <span className="text-sm font-medium">{made} / {r.quantity} made</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase text-white ${badge.cls}`} data-testid={`run-status-${r.id}`}>
                      {badge.icon} {badge.text}
                    </span>
                    {r.bom_name && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase bg-zinc-100 border border-zinc-300 text-zinc-700" data-testid={`run-bom-${r.id}`}>
                        BOM: {r.bom_name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {status === "building" && canEdit && (
                      <Button size="sm" onClick={() => openRecord(r)} data-testid={`record-run-${r.id}`} className="rounded-none bg-[#16A34A] hover:bg-[#15803D] text-white text-xs uppercase font-bold">
                        <CheckCircle size={14} weight="bold" className="mr-1" /> Record Made
                      </Button>
                    )}
                    {status === "building" && canEdit && (
                      <Button size="sm" variant="outline" onClick={() => { setAdjustRun(r); setAdjustDelta(""); }} data-testid={`adjust-run-${r.id}`} className="rounded-none border-zinc-300 text-zinc-700 hover:bg-zinc-100 text-xs uppercase font-bold">
                        Adjust Plan
                      </Button>
                    )}
                    {status === "building" && canEdit && (
                      <Button size="sm" variant="outline" onClick={() => openReject(r)} data-testid={`reject-run-${r.id}`} className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2] text-xs uppercase font-bold">
                        <Warning size={14} weight="bold" className="mr-1" /> On-line Rejection
                      </Button>
                    )}
                    {status === "building" && canEdit && (
                      <Button size="sm" variant="outline" onClick={() => cancelRun(r)} data-testid={`cancel-run-${r.id}`} className="rounded-none border-zinc-300 text-[#DC2626] hover:bg-[#FEF2F2] text-xs uppercase font-bold">
                        <XCircle size={14} weight="bold" className="mr-1" /> Cancel
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => window.open(`/production/${r.id}/timeline`, "_blank", "noopener")} data-testid={`timeline-run-${r.id}`} className="rounded-none border-zinc-300 text-zinc-700 hover:bg-zinc-100 text-xs uppercase font-bold">
                      Timeline
                    </Button>
                    <p className="text-[11px] text-zinc-400">{r.by_name} · {fmt(r.created_at)}</p>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="mt-3">
                  <div className="h-2 bg-zinc-100 w-full" data-testid={`run-progress-${r.id}`}>
                    <div className={`h-2 ${completed ? "bg-[#16A34A]" : cancelled ? "bg-zinc-400" : "bg-[#EA580C]"}`} style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-1">{pct}% complete{status === "building" && remaining > 0 ? ` · ${remaining} remaining` : ""}</p>
                </div>
                {/* Materials: blocked (in progress) or consumed (done) */}
                <div className={`flex flex-wrap gap-x-4 gap-y-1 mt-2 ${cancelled ? "line-through opacity-60" : ""}`}>
                  {(r.lines || []).map((l, i) => {
                    const qty = status === "building" ? Math.round(l.qty_per_unit * remaining * 10000) / 10000 : Math.round(l.qty_per_unit * made * 10000) / 10000;
                    const label = status === "building" ? "blocked" : "used";
                    return <span key={`${l.material_id}-${i}`} className="text-xs text-zinc-500 font-mono">{qty} {l.unit} {l.material_name} <span className="text-zinc-400">({label})</span></span>;
                  })}
                </div>
                {(r.completions || []).length > 0 && (
                  <div className="mt-2 border-t border-zinc-100 pt-2" data-testid={`run-completions-${r.id}`}>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">History</p>
                    {r.completions.map((c, i) => (
                      c.adjustment ? (
                        <p key={`${c.at}-${i}`} className="text-xs text-zinc-600 flex items-center gap-2">
                          <span className="text-[#EA580C] font-bold">⇄</span>
                          Plan {c.quantity_change > 0 ? "+" : ""}<span className="font-mono font-bold">{c.quantity_change}</span> → {c.new_planned} planned · {fmt(c.at)} · {c.by}
                        </p>
                      ) : (
                        <p key={`${c.at}-${i}`} className="text-xs text-zinc-600 flex items-center gap-2">
                          <CheckCircle size={12} weight="fill" className="text-[#16A34A]" />
                          <span className="font-mono font-bold">{c.quantity}</span> × {r.model} made · {fmt(c.at)} · {c.by}
                        </p>
                      )
                    ))}
                  </div>
                )}
                {(r.online_rejections || []).length > 0 && (
                  <div className="mt-2 border-t border-[#FCA5A5] pt-2" data-testid={`run-rejections-${r.id}`}>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#DC2626] mb-1">On-line Rejections (scrapped)</p>
                    {r.online_rejections.map((rj, i) => (
                      <p key={`${rj.at}-${i}`} className="text-xs text-zinc-600 flex flex-wrap items-center gap-2" data-testid={`run-rejection-${r.id}-${i}`}>
                        <span className="text-[#DC2626] font-mono font-bold">✕ {rj.qty} {rj.unit}</span>
                        <span className="text-zinc-700">{rj.material_name}</span>
                        {rj.reason && <><span className="text-zinc-400">·</span><span className="italic">{rj.reason}</span></>}
                        <span className="text-zinc-400">·</span>
                        <span className="text-zinc-500">{fmt(rj.at)} · {rj.by_name}</span>
                      </p>
                    ))}
                  </div>
                )}
                {cancelled && r.cancelled_at && (
                  <p className="text-[11px] text-zinc-500 mt-1">Cancelled by {r.cancelled_by} · {fmt(r.cancelled_at)} · remaining block released</p>
                )}
              </div>
            );});
            })()}
          </div>
        </div>
      </div>

      {/* Adjust planned quantity dialog */}
      <Dialog open={!!adjustRun} onOpenChange={(o) => !o && setAdjustRun(null)}>
        <DialogContent className="rounded-none border-zinc-300 sm:max-w-md" data-testid="adjust-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Adjust Planned Quantity</DialogTitle></DialogHeader>
          {adjustRun && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-zinc-500">
                {adjustRun.order_no} · {adjustRun.model} · planned <span className="font-mono font-bold">{adjustRun.quantity}</span> · already made <span className="font-mono">{adjustRun.made || 0}</span>
              </p>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider">Change to plan (+ to add ovens, − to remove)</Label>
                <Input type="number" data-testid="adjust-delta-input" value={adjustDelta} onChange={(e) => setAdjustDelta(e.target.value)} placeholder="e.g. 2 or -1" className="rounded-none border-zinc-300 font-mono focus-visible:ring-2 focus-visible:ring-primary" />
              </div>
              <p className="text-[11px] text-zinc-400">This changes only the blocked (reserved) raw materials for the remaining ovens. Actual stock and ovens already made are not affected. New plan cannot go below the {adjustRun.made || 0} already made.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustRun(null)} className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={adjustMade} data-testid="adjust-save-button" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Made dialog */}
      <Dialog open={!!recordRun} onOpenChange={(o) => !o && setRecordRun(null)}>
        <DialogContent className="rounded-none border-zinc-300 sm:max-w-md" data-testid="record-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Record Ovens Made</DialogTitle></DialogHeader>
          {recordRun && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-zinc-500">
                {recordRun.model} · BOM {recordRun.bom_name} · <span className="font-mono">{recordRun.made || 0} / {recordRun.quantity}</span> made
                (<span className="font-mono">{recordRun.quantity - (recordRun.made || 0)}</span> remaining)
              </p>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider">Ovens made now</Label>
                <Input type="number" min={1} max={recordRun.quantity - (recordRun.made || 0)} data-testid="record-qty-input" value={recordQty} onChange={(e) => setRecordQty(e.target.value)} className="rounded-none border-zinc-300 font-mono focus-visible:ring-2 focus-visible:ring-primary" />
              </div>
              <p className="text-[11px] text-zinc-400">Raw materials for these ovens will be deducted from stock and released from the block.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecordRun(null)} className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={recordMade} disabled={recording} data-testid="record-save-button" className="rounded-none bg-[#16A34A] hover:bg-[#15803D] text-white font-bold uppercase tracking-wider">{recording ? "Saving…" : "Record Made"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* On-line rejection dialog */}
      <Dialog open={!!rejectRun} onOpenChange={(o) => !o && setRejectRun(null)}>
        <DialogContent className="rounded-none border-zinc-300 sm:max-w-md" data-testid="reject-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">On-line Rejection</DialogTitle></DialogHeader>
          {rejectRun && (() => {
            const line = (rejectRun.lines || []).find((l) => l.material_id === rejectMaterialId);
            const remainingOvens = Math.max((rejectRun.quantity || 0) - (rejectRun.made || 0), 0);
            const alreadyRej = (rejectRun.online_rejections || []).filter((r) => r.material_id === rejectMaterialId).reduce((s, r) => s + (r.qty || 0), 0);
            const orderBlock = line ? Math.max(line.qty_per_unit * remainingOvens - alreadyRej, 0) : 0;
            const avail = matAvailable(rejectMaterialId);
            const maxRej = avail != null ? Math.min(orderBlock, avail) : orderBlock;
            return (
            <div className="space-y-4 py-2">
              <p className="text-sm text-zinc-500">
                {rejectRun.order_no} · {rejectRun.model} · BOM {rejectRun.bom_name}
              </p>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider">Rejected material</Label>
                <Select value={rejectMaterialId} onValueChange={setRejectMaterialId}>
                  <SelectTrigger data-testid="reject-material-select" className="rounded-none border-zinc-300 focus:ring-2 focus:ring-[#DC2626]"><SelectValue placeholder="Select material" /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {(rejectRun.lines || []).map((l) => <SelectItem key={l.material_id} value={l.material_id}>{l.material_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {rejectMaterialId && (
                  <p className="text-[11px] text-zinc-400" data-testid="reject-limits">
                    Blocked for this order: <span className="font-mono text-[#DC2626] font-bold">{orderBlock}</span> {matUnit(rejectMaterialId)}
                    {" "}· Free stock: <span className="font-mono">{avail}</span>
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-[#DC2626]">Quantity scrapped</Label>
                <Input type="number" min="0" max={maxRej} placeholder="0" data-testid="reject-qty-input" value={rejectQty} onChange={(e) => setRejectQty(e.target.value)} className="rounded-none border-zinc-300 font-mono text-right focus-visible:ring-2 focus-visible:ring-[#DC2626]" />
                {Number(rejectQty) > maxRej + 1e-6 && (
                  <p className="text-[11px] text-[#DC2626]" data-testid="reject-over-limit">Cannot exceed the blocked quantity ({maxRej} {matUnit(rejectMaterialId)}).</p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider">Reason</Label>
                <Input placeholder="e.g. bent / cut wrong / damaged" data-testid="reject-reason-input" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-[#DC2626]" />
              </div>
              <p className="text-[11px] text-zinc-400">The scrapped quantity is removed from raw-material stock. The order keeps its full material block — the replacement is compensated from available free stock.</p>
            </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectRun(null)} className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={submitReject} disabled={rejecting} data-testid="reject-save-button" className="rounded-none bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold uppercase tracking-wider">{rejecting ? "Saving…" : "Record Rejection"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BOM editor */}
      <Dialog open={bomOpen} onOpenChange={setBomOpen}>
        <DialogContent className="rounded-none border-zinc-300 sm:max-w-2xl" data-testid="bom-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">{productLabel(activeModel)} · {editBomId ? "Edit BOM" : "New BOM"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2 max-h-[55vh] overflow-y-auto">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">BOM Name *</Label>
              <Input data-testid="bom-name-input" value={bomName} onChange={(e) => setBomName(e.target.value)} placeholder="e.g. Standard, Red Variant, Export" className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
            </div>
            <Label className="text-xs font-bold uppercase tracking-wider block pt-1">Materials per unit</Label>
            {lines.length === 0 && <p className="text-sm text-zinc-400">No lines. Add materials consumed to build one oven.</p>}
            {lines.map((l, i) => (
              <div key={`bomline-${l.material_id || "new"}-${i}`} className="flex items-center gap-2">
                <Select value={l.material_id} onValueChange={(v) => { const n = [...lines]; n[i].material_id = v; setLines(n); }}>
                  <SelectTrigger data-testid={`bom-material-${i}`} className="rounded-none border-zinc-300 flex-1 focus:ring-2 focus:ring-primary"><SelectValue placeholder="Select material" /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="number" placeholder="Qty/unit" data-testid={`bom-qty-${i}`} value={l.qty_per_unit} onChange={(e) => { const n = [...lines]; n[i].qty_per_unit = e.target.value; setLines(n); }} className="rounded-none border-zinc-300 font-mono w-28 focus-visible:ring-2 focus-visible:ring-primary" />
                <span className="text-xs text-zinc-400 w-10">{matUnit(l.material_id)}</span>
                <button onClick={() => setLines(lines.filter((_, x) => x !== i))} className="text-[#DC2626] p-1"><Trash size={16} weight="bold" /></button>
              </div>
            ))}
            <Button variant="outline" onClick={() => setLines([...lines, { material_id: "", qty_per_unit: "" }])} data-testid="bom-add-line" className="rounded-none border-zinc-300 text-xs uppercase font-bold">
              <Plus size={14} weight="bold" className="mr-1" /> Add Material
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBomOpen(false)} className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={saveBom} disabled={savingBom} data-testid="bom-save-button" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider">{savingBom ? "Saving…" : editBomId ? "Update BOM" : "Save BOM"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
